import React, { SetStateAction, Dispatch, useState, useRef } from 'react';
import Link from 'next/link';
import NotificationModalRow from '@/components/NotificationModalRow';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { INotification } from '@/types/api/notifications';
import { getFirstValueTrue } from '@/utils/arrayUtils';
import { ROUTER_PAGES } from '@/utils/constants';
import { handleErrorAsync } from '@/utils/handlers';
import { Popper } from '@mui/base/Popper';
import { MoreVert } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Divider,
  Modal,
  Typography,
  IconButton,
  useMediaQuery,
  Button,
  ClickAwayListener,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface INotificationModalProps {
  notifications: Array<INotification>;
  setNotifications: Dispatch<SetStateAction<INotification[]>>;
  isOpen: boolean;
  onClose: () => void;
  notificationsLimit?: number;
}

interface INotificationsListProps {
  notifications: INotification[];
  notificationsLimit?: number;
}

const NotificationsList = (props: INotificationsListProps) => {
  const { notifications, notificationsLimit } = props;

  const Component = getFirstValueTrue([
    notifications.length === 0 && (
      <Typography
        sx={{
          padding: 2,
          color: 'text.secondary',
          textAlign: 'center',
        }}
        variant="body2"
      >
        Não há novas notificações
      </Typography>
    ),
    <>
      {notifications.slice(0, notificationsLimit).map(notification => (
        <NotificationModalRow
          isRead={notification.read}
          notification={notification}
          key={notification.id}
          truncMessage
        />
      ))}
    </>,
  ]);

  return <>{Component}</>;
};

function NotificationModal({
  notifications,
  setNotifications,
  isOpen,
  onClose,
  notificationsLimit = 7,
}: INotificationModalProps) {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));
  const smDownHeight = useMediaQuery('(max-height:700px)');
  const { setIsLoading } = useLoading();

  const anchorEl = useRef(null);
  const [openOptions, setOpenOptions] = useState(false);

  const handleClose = () => onClose();

  const readAllNotifications = async () => {
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      await api.post('/api/notifications/read_all/');
    }).finally(() => setIsLoading(false));

    if (error) {
      return Toast(
        'error',
        error.message || 'Erro ao marcar todas as notificações como lidas'
      );
    }
    setNotifications(prevState =>
      prevState.map(notification => {
        notification.read = true;
        return notification;
      })
    );

    setOpenOptions(false);
  };

  const handleClickAway = () => setOpenOptions(false);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      disableScrollLock={smUp}
      aria-label="modal-notifications"
      aria-labelledby="modal-notifications"
      aria-describedby="modal-notifications-description"
      sx={{
        '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0)' },
      }}
    >
      <>
        <Box
          data-testid="modal-container"
          sx={{
            height: '100%',
            borderRadius: '16px',
            ...(smUp && {
              boxShadow: '1px 10px 10px rgb(0, 0, 0, 0.5)',
              width: '350px',
              height: smDownHeight ? '80vh' : 'inherit',
              position: 'absolute',
              right: 10,
              top: 61,
              ':after': {
                content: '""',
                position: 'absolute',
                borderLeft: '10px solid transparent',
                borderRight: ' 10px solid transparent',
                borderBottom: `18px solid ${theme.palette.primary.contrastText}`,
                top: '-2%',
                right: '3%',
              },
            }),
          }}
        >
          <Box
            sx={theme => ({
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              pb: '52px',
              backgroundColor: 'primary.contrastText',
              [theme.breakpoints.up('sm')]: {
                borderRadius: '16px',
              },
            })}
          >
            {!smUp && (
              <>
                <Box
                  component="header"
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                    }}
                  >
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={handleClose}
                      aria-label="button-close-modal"
                    >
                      <CloseIcon />
                    </IconButton>

                    <Typography variant="h6">Notificações</Typography>
                  </Box>
                  <IconButton
                    size="small"
                    color="inherit"
                    aria-label="button-more-options"
                    ref={anchorEl}
                    onClick={e => {
                      e.stopPropagation();
                      setOpenOptions(true);
                    }}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>

                <Divider />
              </>
            )}

            <Box
              className={!smDownHeight ? 'hidden-scrollbar' : ''}
              sx={{
                overflowY: 'auto',
                width: '100%',
              }}
            >
              {smUp && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '18px 18px 14px 18px',
                  }}
                >
                  <Typography variant="subtitle2">Notificações</Typography>
                  <Button
                    sx={{ textTransform: 'none' }}
                    onClick={() => readAllNotifications()}
                  >
                    <Typography variant="body2">
                      Marcar todas como lidas
                    </Typography>
                  </Button>
                </Box>
              )}
              <NotificationsList
                notifications={notifications}
                notificationsLimit={notificationsLimit}
              />
            </Box>

            <Box
              component="footer"
              sx={{
                bottom: 0,
                position: 'absolute',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Divider />
              <Link passHref href={ROUTER_PAGES.notificacoes}>
                <Box
                  component="a"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                  }}
                  onClick={handleClose}
                  data-testid="link-to-all-notifications"
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: '500',
                    }}
                  >
                    Ver todas
                  </Typography>
                </Box>
              </Link>
            </Box>
          </Box>
        </Box>

        <ClickAwayListener onClickAway={handleClickAway}>
          <Popper
            open={openOptions}
            anchorEl={anchorEl.current}
            disablePortal
            placement="bottom-start"
            style={{ zIndex: 2 }}
          >
            <Box
              sx={{
                mx: 1,
                my: 2,
                cursor: 'pointer',
                backgroundColor: '#fff',
                width: '290px',
                height: '68px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button
                sx={{ textTransform: 'none' }}
                onClick={() => readAllNotifications()}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(0, 0, 0, 0.5)' }}
                >
                  Marcar todas como lidas
                </Typography>
              </Button>
            </Box>
          </Popper>
        </ClickAwayListener>
      </>
    </Modal>
  );
}

export type { INotificationModalProps };
export default NotificationModal;
